/*
 * Feature pages (src/blog/features.ts): the hero's live example tour and muted videos.
 *
 * The tour starts as a poster with a link to the viewer (works without JS). On desktops
 * with a mouse the poster turns into the live iframe as it scrolls near; on touch screens
 * only after a tap, because a full-width 3D viewer would swallow the page's scroll and
 * three.js is heavy on a phone's data plan. The iframe asks for `lite=1`, so the viewer
 * stays on the 4096 panos (~2 MB a room) instead of upgrading to the 8192 ones (4–5 MB).
 *
 * Videos ship with preload="none" and no autoplay attribute, so a page doesn't download
 * them up front: each one starts when it scrolls into view and pauses when it leaves.
 * With reduced motion they stay paused and get controls.
 */
const AUTO_LOAD = '(min-width: 900px) and (hover: hover) and (pointer: fine)';

/** The viewer needs WebGL; without it the poster and its link to /tour stay. */
function hasWebGL(): boolean {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(canvas.getContext('webgl2') ?? canvas.getContext('webgl'));
  } catch {
    return false;
  }
}

function mountTour(frame: HTMLElement): void {
  const src = frame.dataset.tourEmbed;
  if (!src || frame.dataset.loaded) return;
  frame.dataset.loaded = '1';
  const iframe = document.createElement('iframe');
  iframe.src = src;
  iframe.title = frame.dataset.tourTitle ?? '';
  iframe.allow = 'fullscreen; gyroscope; accelerometer';
  iframe.setAttribute('allowfullscreen', '');
  frame.append(iframe);
  frame.classList.add('is-live');
}

for (const frame of hasWebGL() ? document.querySelectorAll<HTMLElement>('[data-tour-embed]') : []) {
  frame.querySelector('.fp-play')?.addEventListener('click', (event) => {
    event.preventDefault();
    mountTour(frame);
  });
  if (window.matchMedia(AUTO_LOAD).matches && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        mountTour(frame);
      },
      { rootMargin: '200px 0px' },
    );
    observer.observe(frame);
  }
}

const videos = document.querySelectorAll<HTMLVideoElement>('video[data-autoplay]');
if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) {
  for (const video of videos) video.controls = true;
} else {
  const observer = new IntersectionObserver((entries) => {
    for (const { target, isIntersecting } of entries) {
      const video = target as HTMLVideoElement;
      if (isIntersecting) void video.play().catch(() => (video.controls = true));
      else video.pause();
    }
  });
  for (const video of videos) observer.observe(video);
}
